import { CommonModule } from '@angular/common';
import {
  Component, computed, effect, forwardRef, input, model,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { FieldState } from '../../../models/field.model';
import { InlineSvgComponent } from '../../inline-svg/inline-svg.component';

@Component({
  selector: 'ui-password-field',
  imports: [ CommonModule, InlineSvgComponent, FormsModule ],
  templateUrl: './password-field.component.html',
  styleUrl: './password-field.component.scss',
  providers: [ {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => PasswordFieldComponent),
    multi: true,
  } ],
})
export class PasswordFieldComponent implements ControlValueAccessor {
  /** Defines the style of the currency field. */
  fieldState = FieldState;
  /** A unique name identifier for the password component.
   *  The name is a string generated with a random hexadecimal number.
  */
  name = input<string>(`ui-password-field-${(Math.round((Math.random() * 100000000)).toString(16))}`);
  /** Output bind value. */
  value = model<string>('');
  /** Defines the current style of the currency field. */
  state = input<FieldState>(FieldState.Default);
  /** Defines the placeholder  of the password field. */
  placeholder = input<string>('');
  /** Alert text message to be displayed based on the value. */
  alert = input<string>();
  /** Defines if the password should be visible. */
  showPassword = false;

  /** Computed property that returns the icon
   * that should be displayed based on the FieldState.
  */
  icon = computed(() => {
    switch (this.state()) {
      case FieldState.Success:
      case FieldState.Info:
        return 'success';
      case FieldState.Invalid:
        return 'error';
      default:
        return '';
    }
  });

  constructor() {
    effect(() => {
      this.onChange(this.value() ?? '');
    });
  }

  onBlur(): void {
    this.onTouched();
  }

  /** Method that toggles the password visibility. */
  onShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  writeValue(val: string): void {
    this.value.set(val ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: string) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};
}
